import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NpsItemComponent } from './nps-item.component';
import { ButtonComponent } from '../button';
import { ClientAppId } from '@sc/commons';
import { CLIENT_APP_ID } from '@sc/data-access-models';

export interface NpsResult {
  score: number | null;
  comment?: string;
  userSkipped?: boolean;
  appOrigin: ClientAppId;
  timestamp: Date;
}

@Component({
  selector: 'sc-nps-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    TranslateModule,
    NpsItemComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./nps-dialog.component.scss'],
  template: `
    <div class="nps-dialog-container">
      <mat-card>
        @if (selectedScore === null && !userSkipped) {

        <mat-card-content>
          <p class="title">
            {{ 'NpsDialog.question' | translate }}
          </p>
          <div class="nps-scores">
            @for(score of scores; track score) {
            <sc-nps-item
              [score]="score"
              (scoreSelected)="onScoreSelected($event)"
            ></sc-nps-item>
            }
          </div>
        </mat-card-content>
        <mat-card-actions>
          <sc-button (click)="onAskLater()" buttonType="secondary">
            {{ 'NpsDialog.askLaterButton' | translate }}
          </sc-button>
        </mat-card-actions>

        } @else if (selectedScore && selectedScore<= 2) {

        <mat-card-content>
          <p class="title">
            {{ 'NpsDialog.feedbackNegativeMessage' | translate }}
          </p>
          <div class="nps-comment">
            <label>{{ 'NpsDialog.commentLabel' | translate }}</label>
            <textarea
              [(ngModel)]="comment"
              rows="3"
              [placeholder]="'NpsDialog.commentPlaceholder' | translate"
            ></textarea>
          </div>
        </mat-card-content>
        <mat-card-actions>
          <sc-button (click)="onSubmit()" buttonType="primary">
            {{ 'NpsDialog.closeButton' | translate }}
          </sc-button>
          <sc-button (click)="onPrevious()" buttonType="secondary">
            {{ 'NpsDialog.previousButton' | translate }}
          </sc-button>
        </mat-card-actions>

        } @else if(selectedScore && selectedScore <= 4) {

        <mat-card-content>
          <p class="title">
            {{ 'NpsDialog.feedbackNeutralMessage.title' | translate }}
          </p>
          <p>
            {{ 'NpsDialog.feedbackNeutralMessage.content' | translate }}
          </p>
          <div class="nps-comment">
            <label>{{ 'NpsDialog.commentLabel' | translate }}</label>
            <textarea
              matInput
              [(ngModel)]="comment"
              rows="3"
              [placeholder]="'NpsDialog.commentPlaceholder' | translate"
            ></textarea>
          </div>
        </mat-card-content>
        <mat-card-actions>
          <sc-button (click)="onSubmit()" buttonType="primary">
            {{ 'NpsDialog.closeButton' | translate }}
          </sc-button>
          <sc-button (click)="onPrevious()" buttonType="secondary">
            {{ 'NpsDialog.previousButton' | translate }}
          </sc-button>
        </mat-card-actions>

        } @else if (selectedScore === 5) {

        <mat-card-content>
          <p class="title">
            {{ 'NpsDialog.feedbackPositiveMessage.title' | translate }}
          </p>
          <p>
            {{ 'NpsDialog.feedbackPositiveMessage.content' | translate }}
          </p>
        </mat-card-content>
        <mat-card-actions>
          <sc-button (click)="onClose()" buttonType="secondary">
            {{ 'NpsDialog.closeButton' | translate }}
          </sc-button>
        </mat-card-actions>

        } @else {

        <mat-card-content>
          <p class="title">
            {{ 'NpsDialog.askLaterMessage.title' | translate }}
          </p>
          <p>
            {{ 'NpsDialog.askLaterMessage.content' | translate }}
          </p>
        </mat-card-content>
        <mat-card-actions>
          <sc-button (click)="onClose()" buttonType="primary">{{
            'NpsDialog.closeButton' | translate
          }}</sc-button>
        </mat-card-actions>
        }
      </mat-card>
    </div>
  `,
})
export class NpsDialogComponent {
  scores: readonly number[] = [1, 2, 3, 4, 5];
  selectedScore: number | null = null;
  userSkipped = false;

  comment = '';

  // Emits a partial result immediately when a score is selected (no close)
  @Output() scoreSelectedResult = new EventEmitter<NpsResult>();

  private isClosing = false;
  private readonly sessionTimestamp = new Date();

  constructor(
    public dialogRef: MatDialogRef<NpsDialogComponent>,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {}

  private buildResult(options?: { skip?: boolean }): NpsResult {
    const skip = options?.skip ?? this.userSkipped;
    const trimmed = this.comment.trim();
    return {
      score: skip ? null : this.selectedScore,
      comment: skip ? undefined : trimmed || undefined,
      userSkipped: skip || undefined,
      appOrigin: this.clientAppId,
      timestamp: this.sessionTimestamp,
    };
  }

  onScoreSelected(score: number): void {
    this.selectedScore = score;

    // Emit partial result immediately so host can save the score selection
    this.scoreSelectedResult.emit(this.buildResult());
  }

  onSubmit(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    const result: NpsResult = this.buildResult();
    this.closeWithResult(result);
  }

  onAskLater(): void {
    if (this.isClosing) return;
    this.userSkipped = true;
    this.isClosing = true;
    const result: NpsResult = this.buildResult({ skip: true });
    this.closeWithResult(result);
  }

  onPrevious(): void {
    // Go back to score selection without saving.
    this.selectedScore = null;
    this.comment = '';
  }

  onClose(): void {
    // If user previously chose "Ask later", we've already saved.
    // Otherwise, treat Close as a final action and save now (e.g., for positive flow).
    if (this.isClosing) return;
    if (this.userSkipped) {
      this.dialogRef.close();
    } else {
      this.isClosing = true;
      const result: NpsResult = this.buildResult();
      this.closeWithResult(result);
    }
  }

  private closeWithResult(result: NpsResult): void {
    this.dialogRef.close(result);
  }
}
